"use client";

import { useState, useEffect } from "react";

interface TonyAvatarProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  variant?: "default" | "thinking";
}

const sizeMap = {
  sm: "w-8 h-8",
  md: "w-12 h-12 sm:w-14 sm:h-14",
  lg: "w-32 h-32 sm:w-40 sm:h-40",
  xl: "w-40 h-40 sm:w-48 sm:h-48",
};

export function TonyAvatar({
  size = "md",
  className = "",
  variant = "default",
}: TonyAvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Fallback: 이모지 표시
  const fallbackEmoji = (
    <div
      className={`relative ${sizeMap[size]} rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white ${size === "lg" || size === "xl" ? "text-4xl" : size === "md" ? "text-2xl" : "text-xl"} ${className}`}
      role="img"
      aria-label="토니"
    >
      <span aria-hidden>🦊</span>
      {variant === "thinking" && (
        <>
          <span
            aria-hidden
            className="absolute -right-1 -bottom-1 text-[18px] sm:text-[20px]"
          >
            🤔
          </span>
          <span
            aria-hidden
            className="absolute -left-1 -top-1 flex items-center justify-center w-6 h-6 rounded-full bg-white/70 text-purple-700 text-[14px] font-bold"
          >
            ?
          </span>
        </>
      )}
    </div>
  );

  // 컴포넌트 마운트 시 이미지 로드 테스트
  useEffect(() => {
    console.log("🦊 TonyAvatar 컴포넌트 마운트됨, 이미지 경로: /fox.jpg");
    const img = new Image();
    
    img.onload = () => {
      console.log("✅ 토니 이미지 로딩 성공: /fox.jpg");
      setImageLoaded(true);
      setImageError(false);
    };
    
    img.onerror = (e) => {
      console.error("❌ 토니 이미지 로딩 실패: /fox.jpg", e);
      setImageError(true);
      setImageLoaded(false);
    };
    
    img.src = "/fox.jpg";
  }, []);

  // 이미지 로딩 실패 시 fallback 표시
  if (imageError) {
    console.log("🦊 토니 이미지 실패로 이모지 fallback 표시");
    return fallbackEmoji;
  }

  // 이미지가 아직 로딩 중이면 일단 fallback 표시 (로딩 중에도 보이도록)
  if (!imageLoaded) {
    return fallbackEmoji;
  }

  // 이미지 로드 성공 시 이미지 표시
  return (
    <div className={`relative ${sizeMap[size]} flex-shrink-0 ${className}`}>
      <img
        src="/fox.jpg"
        alt="토니"
        className="w-full h-full object-cover rounded-full border-2 border-purple-300"
        onError={() => {
          console.error("❌ 토니 이미지 렌더링 실패: /fox.jpg");
          setImageError(true);
        }}
        onLoad={() => {
          console.log("✅ 토니 이미지 렌더링 성공");
        }}
      />
      {variant === "thinking" && !imageError && (
        <>
          <span
            aria-hidden
            className="absolute -right-1 -bottom-1 text-[18px] sm:text-[20px]"
          >
            🤔
          </span>
          <span
            aria-hidden
            className="absolute -left-1 -top-1 flex items-center justify-center w-6 h-6 rounded-full bg-white/70 text-purple-700 text-[14px] font-bold"
          >
            ?
          </span>
        </>
      )}
    </div>
  );
}

